import PropTypes from "prop-types";
import React from "react";
import { MenuItem, Select, Stack } from "@mui/material";

import { ChevronRight } from "../../Assets/Icons";

export const MenuPaperProps = {
  sx: {
    backgroundColor: "var(--bg-primary)",
    borderRadius: "var(--rounded-xl)",
    border: "var(--card-border)",
  },
};

export const MenuItemProps = {
  sx: {
    minHeight: "auto",
    font: "normal normal 400 14px/20px Poppins, sans-serif",
    color: "var(--mist)",
    "&.Mui-selected": {
      color: "var(--charcoal) !important",
      backgroundColor: "var(--primary-400) !important",

      "& svg path[fill]": {
        fill: "var(--charcoal)",
      },

      "& svg path[stroke]": {
        stroke: "var(--charcoal)",
      },
    },
    "&:not(.Mui-selected):hover": {
      backgroundColor: "rgb(from var(--mist) r g b / 10%)",
    },
    // WCAG 1.4.11 — keyboard-focused option needs a focus indicator with
    // >= 3:1 contrast against the menu paper. A subtle background tint
    // alone landed at ~1.5:1; an inset ring of --primary-400 over
    // --bg-primary measures ~10:1.
    "&:not(.Mui-selected).Mui-focused, &:not(.Mui-selected).Mui-focusVisible": {
      backgroundColor: "rgb(from var(--mist) r g b / 20%)",
      boxShadow: "inset 0 0 0 2px var(--primary-400)",
    },
  },
};

export const MenuListProps = {
  sx: {
    maxHeight: "300px",
    "& .MuiMenuItem-root": { ...MenuItemProps.sx },
  },
};

const SelectField = React.forwardRef(
  (
    {
      className = "",
      label,
      description,
      options,
      errorMessage,
      helperText,
      dropUp = false,
      id: idProp,
      inputProps = {},
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = idProp || (props.name ? `sf-${props.name}` : `sf-${generatedId}`);
    const labelId = label ? `${inputId}-label` : undefined;
    const descriptionId = description ? `${inputId}-description` : undefined;
    const errorId = errorMessage ? `${inputId}-error` : undefined;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const describedBy = [descriptionId, errorId, helperId].filter(Boolean).join(" ") || undefined;

    return (
      <div className={`select-field ${className}`}>
        {!!label && <label htmlFor={inputId} id={labelId}>{label}</label>}
        {!!description && <span className="description" id={descriptionId}>{description}</span>}
        <div className="input-icon-wrapper">
          <Select
            variant="outlined"
            IconComponent={ChevronRight}
            id={inputId}
            labelId={labelId}
            inputProps={{
              ...(describedBy ? { "aria-describedby": describedBy } : {}),
              ...(!label && props["aria-label"] ? {} : !label ? { "aria-label": "Select an option" } : {}),
              ...inputProps,
            }}
            {...props}
            inputRef={ref}
            MenuProps={{
              PaperProps: MenuPaperProps,
              MenuListProps,
              anchorOrigin: dropUp
                ? { vertical: "top", horizontal: "left" }
                : { vertical: "bottom", horizontal: "left" },
              transformOrigin: dropUp
                ? { vertical: "bottom", horizontal: "left" }
                : { vertical: "top", horizontal: "left" },
            }}
          >
            {options?.length === 0 && (
              <Stack
                className="text-base-medium color--american-silver"
                alignItems="center"
                justifyContent="center"
                sx={{ height: "300px" }}
              >
                No options available
              </Stack>
            )}
            {options?.map((option, idx) => (
              <MenuItem key={idx} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>
        {!!errorMessage && <span className="error-message">{errorMessage}</span>}
        {!!helperText && <span className="helper-text">{helperText}</span>}
      </div>
    );
  }
);

SelectField.prototype = {
  className: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.array,
  dropUp: PropTypes.bool,
  id: PropTypes.string,
};

export default SelectField;
